import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Play, Pause, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const SS = {
  hero: '/screenshots/screenshot-23.png',
  scheduling: '/screenshots/screenshot-21.png',
  kanban: '/screenshots/screenshot-22.png',
  voice: '/screenshots/screenshot-24.png',
  analytics: '/screenshots/screenshot-25.png',
  waitlist: '/screenshots/screenshot-26.png',
  reminders: '/screenshots/screenshot-27.png',
  flow: '/screenshots/screenshot-28.png',
  reports: '/screenshots/screenshot-29.png',
};

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } },
};
const fadeUpHeading = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] } },
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const slides = [
  { eyebrow: 'Patient Flow', title: 'Every patient, tracked.', body: 'Kanban-style board shows patient status from arrival to checkout. Six columns, color-coded, updating in real time.', img: SS.hero },
  { eyebrow: 'Smart Scheduling', title: 'Gaps filled instantly.', body: 'Echo detects cancellations and automatically reaches out to waitlisted patients. Your schedule stays full without staff effort.', img: SS.scheduling },
  { eyebrow: 'AI Voice Agent', title: 'Calls handled 24/7.', body: 'Echo answers patient calls, confirms appointments, reschedules, and handles routine questions — around the clock.', img: SS.voice },
  { eyebrow: 'Analytics', title: 'Every metric, visible.', body: 'No-show rates, schedule utilization, wait times, and provider efficiency — all tracked and trended automatically.', img: SS.analytics },
];

const stickyBlocks = [
  { eyebrow: 'Before the day starts', h3: 'Your schedule is already optimized.', body: 'Echo fills cancellations overnight by reaching out to waitlisted patients. By the time your staff arrives, the day\'s schedule is full and confirmations are sent.', pill: '35% fewer no-shows', img: SS.scheduling },
  { eyebrow: 'During the day', h3: 'Every patient, visible.', body: 'The Patient Flow board shows exactly where every patient is — Scheduled, Arrived, Waiting Room, In Exam, With Provider, In Procedure. Color-coded. Real-time.', pill: 'Zero status guessing', img: SS.hero },
  { eyebrow: 'Around the clock', h3: 'Calls answered. Always.', body: 'Echo\'s AI voice agent handles incoming calls 24/7 — confirming appointments, answering FAQs, routing urgent issues, and rescheduling without hold times.', pill: '24/7 availability', img: SS.voice },
  { eyebrow: 'End of day', h3: 'Know what happened today.', body: 'Daily reports show schedule utilization, average wait times, no-show patterns, and provider efficiency. Identify bottlenecks before they become problems.', pill: '95% schedule utilization', img: SS.reports },
];

const featureCards = [
  { name: 'Smart Scheduling', desc: 'AI-powered scheduling that fills gaps, manages waitlists, and optimizes provider time.', img: SS.scheduling },
  { name: 'AI Voice Agent', desc: 'Handles calls 24/7 — confirmations, rescheduling, FAQs, and urgent routing.', img: SS.voice },
  { name: 'Patient Flow Board', desc: 'Real-time Kanban view of every patient from check-in to checkout.', img: SS.hero },
  { name: 'Intelligent Waitlist', desc: 'Automatically contacts waitlisted patients when slots open up.', img: SS.waitlist },
  { name: 'Smart Reminders', desc: 'Multi-channel reminders via SMS, email, and voice — timed for maximum show rates.', img: SS.reminders },
  { name: 'Scheduling Analytics', desc: 'No-show trends, utilization rates, wait times, and provider efficiency dashboards.', img: SS.analytics },
];

const HeroSection = () => {
  const mockupRef = useRef(null);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 180], [72, 0]);
  const rotX = useTransform(scrollY, [0, 180], [6, 0]);
  const sc = useTransform(scrollY, [0, 180], [0.97, 1]);
  const op = useTransform(scrollY, [0, 180], [0.75, 1]);

  return (
    <section className="relative pt-32 pb-0 white-texture" style={{ background: '#0a0a0a' }} data-testid="echo-hero">
      <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-[680px] mx-auto px-4 text-center">
        <motion.p variants={fadeUp} className="text-sm font-medium tracking-[0.1em] uppercase" style={{ color: '#888' }}>Scheduling Intelligence</motion.p>
        <motion.h1 variants={fadeUpHeading} className="text-[40px] md:text-[56px] lg:text-[72px] text-white font-bold leading-[1.1] tracking-[-0.03em] mt-4">
          Every slot filled. Every patient reminded.
        </motion.h1>
        <motion.p variants={fadeUp} className="text-lg text-[#aaaaaa] leading-[1.6] max-w-[560px] mx-auto mt-4 font-normal">
          Echo is the always-on AI that reduces no-shows, handles patient communication, fills cancellations instantly, and keeps your schedule running at full capacity — without adding headcount.
        </motion.p>
        <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 mt-8">
          <Link to="/contact" className="h-11 px-6 rounded-lg text-black text-sm font-medium flex items-center gap-2" style={{ background: '#ffffff' }} data-testid="echo-cta-trial">
            Start free trial
          </Link>
          <button className="h-11 px-6 rounded-lg text-white text-sm font-medium flex items-center gap-2" style={{ border: '0.5px solid #2a2a2a' }} data-testid="echo-cta-demo">
            See it in action
          </button>
        </motion.div>
      </motion.div>
      <div className="relative mt-14 flex justify-center" style={{ perspective: '1200px' }}>
        <motion.div ref={mockupRef} style={{ y, rotateX: rotX, scale: sc, opacity: op, willChange: 'transform, opacity' }} className="w-[88vw] max-w-[1080px]">
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="h-10 flex items-center px-3.5" style={{ background: '#242424', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full" style={{ background: '#ff5f57' }} />
                <div className="w-3 h-3 rounded-full" style={{ background: '#febc2e' }} />
                <div className="w-3 h-3 rounded-full" style={{ background: '#28c840' }} />
              </div>
              <div className="mx-auto h-[26px] px-4 rounded-md flex items-center" style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.08)' }}>
                <span className="text-[#555] text-xs font-mono">app.ariaone.ai/echo</span>
              </div>
            </div>
            <img src={SS.hero} alt="Echo Patient Flow" className="w-full object-cover object-top" style={{ height: 580 }} />
          </div>
          <div className="absolute bottom-[-20px] left-1/2 -translate-x-1/2 w-[60%] h-[80px] rounded-full" style={{ background: 'rgba(107,92,231,0.1)', filter: 'blur(60px)' }} />
        </motion.div>
      </div>
    </section>
  );
};

const stats = [
  { num: '35%', label: 'fewer no-shows' },
  { num: '95%', label: 'schedule utilization' },
  { num: '24/7', label: 'AI voice agent' },
  { num: '0', label: 'missed patient calls' },
];

const StatBand = () => (
  <section className="w-full py-10" style={{ background: '#111111', borderTop: '0.5px solid #2a2a2a', borderBottom: '0.5px solid #2a2a2a' }} data-testid="echo-stat-band">
    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-[1080px] mx-auto px-4 flex items-center justify-between">
      {stats.map((s, i) => (
        <div key={i} className="flex items-center">
          {i > 0 && <div className="h-10 mr-8 lg:mr-12" style={{ width: '0.5px', background: '#2a2a2a' }} />}
          <motion.div variants={fadeUp} className="text-center flex-1">
            <div className="text-white text-3xl md:text-[40px] font-bold">{s.num}</div>
            <div className="text-[#555] text-xs mt-1">{s.label}</div>
          </motion.div>
        </div>
      ))}
    </motion.div>
  </section>
);

const HighlightCarousel = () => {
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timerRef = useRef(null);
  const advance = useCallback(() => setActive(p => (p + 1) % slides.length), []);

  useEffect(() => {
    if (!playing) return;
    timerRef.current = setInterval(advance, 5000);
    return () => clearInterval(timerRef.current);
  }, [playing, advance]);

  return (
    <section className="py-20 md:py-28 white-texture" style={{ background: '#0a0a0a' }} data-testid="echo-carousel">
      <div className="max-w-[1200px] mx-auto px-4">
        <h2 className="text-[32px] text-white font-bold mb-8">Get the highlights</h2>
      </div>
      <div className="flex justify-center">
        <div className="w-[88vw] max-w-[1200px] rounded-3xl overflow-hidden relative" style={{ background: '#111111', height: 520 }} onMouseEnter={() => setPlaying(false)} onMouseLeave={() => setPlaying(true)}>
          <AnimatePresence mode="wait">
            <motion.div key={active} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} className="absolute inset-0 flex">
              <div className="w-[38%] p-12 flex flex-col justify-center">
                <p className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>{slides[active].eyebrow}</p>
                <h3 className="text-[28px] text-white font-bold leading-[1.2] mt-3">{slides[active].title}</h3>
                <p className="text-[#aaa] text-base leading-[1.7] mt-3 max-w-[300px] font-normal">{slides[active].body}</p>
              </div>
              <div className="w-[62%] relative">
                <img src={slides[active].img} alt={slides[active].title} className="absolute inset-0 w-full h-full object-cover object-top" />
              </div>
            </motion.div>
          </AnimatePresence>
          <div className="absolute bottom-5 left-12 flex items-center gap-3 z-10">
            <button onClick={() => setPlaying(p => !p)} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.08)' }} data-testid="echo-carousel-play">
              {playing ? <Pause className="w-3.5 h-3.5 text-white" /> : <Play className="w-3.5 h-3.5 text-white" />}
            </button>
            <div className="flex gap-1.5">
              {slides.map((_, i) => (
                <button key={i} onClick={() => setActive(i)} className="h-1 rounded-full overflow-hidden" style={{ width: 40, background: 'rgba(255,255,255,0.1)' }}>
                  <motion.div className="h-full rounded-full" style={{ background: '#ffffff' }} initial={{ width: 0 }} animate={{ width: i === active && playing ? '100%' : i === active ? '100%' : '0%' }} transition={{ duration: i === active && playing ? 5 : 0.2, ease: 'linear' }} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const StickyScrollSection = () => {
  const [activeBlock, setActiveBlock] = useState(0);
  const blockRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) setActiveBlock(Number(entry.target.dataset.idx));
      });
    }, { threshold: 0.4 });
    blockRefs.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="white-texture" style={{ background: '#0a0a0a' }} data-testid="echo-sticky-scroll">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-[640px] mx-auto text-center px-4 pb-20">
        <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>How it works</motion.p>
        <motion.h2 variants={fadeUpHeading} className="text-3xl md:text-[48px] text-white font-bold tracking-tight leading-[1.15] mt-4">From empty slots to a full schedule — without a single phone call.</motion.h2>
        <motion.p variants={fadeUp} className="text-[17px] text-[#aaa] mt-4 font-normal">Echo runs your scheduling and patient flow automatically. Here's what that looks like.</motion.p>
      </motion.div>
      <div className="relative" style={{ minHeight: '500vh' }}>
        <div className="flex">
          <div className="w-[45%] px-8 md:px-14">
            {stickyBlocks.map((block, i) => (
              <div key={i} ref={el => (blockRefs.current[i] = el)} data-idx={i} className="py-20 md:py-28" style={{ minHeight: '100vh' }}>
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
                  <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>{block.eyebrow}</motion.p>
                  <motion.h3 variants={fadeUpHeading} className="text-2xl md:text-3xl text-white font-bold tracking-tight mt-3">{block.h3}</motion.h3>
                  <motion.p variants={fadeUp} className="text-[17px] text-[#aaa] leading-[1.7] mt-4 max-w-[420px] font-normal">{block.body}</motion.p>
                  <motion.div variants={fadeUp} className="inline-block mt-4 px-3.5 py-1 rounded-full text-[13px]" style={{ background: 'rgba(255,255,255,0.06)', border: '0.5px solid rgba(255,255,255,0.15)', color: '#aaa' }}>{block.pill}</motion.div>
                </motion.div>
              </div>
            ))}
          </div>
          <div className="w-[55%] sticky top-0 h-screen flex items-center p-10">
            <div className="w-full rounded-2xl overflow-hidden" style={{ background: '#111111', border: '0.5px solid #2a2a2a', height: 'calc(100vh - 80px)' }}>
              <AnimatePresence mode="wait">
                <motion.img key={activeBlock} src={stickyBlocks[activeBlock].img} alt={stickyBlocks[activeBlock].h3} className="w-full h-full object-cover object-top rounded-2xl" initial={{ opacity: 0, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} />
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const FullWidthBreak = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '10%']);
  return (
    <section ref={ref} className="relative w-full overflow-hidden" style={{ height: '60vh' }} data-testid="echo-fullwidth-break">
      <motion.img src={SS.kanban} alt="Echo Patient Flow" className="absolute inset-0 w-full h-[120%] object-cover object-top" style={{ y: imgY }} />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 65%, #0a0a0a 100%)' }} />
    </section>
  );
};

const VoiceEditorialSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '8%']);
  const checks = ['Confirms and reschedules appointments', 'Answers common patient questions 24/7', 'Routes urgent calls to on-call staff'];
  return (
    <section ref={ref} className="w-full" style={{ background: '#0a0a0a' }} data-testid="echo-voice-editorial">
      <div className="flex flex-col lg:flex-row min-h-[600px]">
        <motion.div className="w-full lg:w-1/2 px-8 md:px-16 lg:px-20 flex flex-col justify-center py-16" initial={{ opacity: 0, x: -32 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
          <p className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>AI Voice Agent</p>
          <h2 className="text-3xl md:text-[48px] text-white font-bold leading-[1.15] tracking-tight max-w-[460px] mt-4">Your front desk. Always on.</h2>
          <p className="text-[17px] text-[#aaa] leading-[1.7] max-w-[420px] mt-4 font-normal">
            Echo's AI voice agent answers patient calls around the clock — confirming appointments, handling rescheduling, and routing urgent issues to the right person. No hold times, no missed calls.
          </p>
          <div className="mt-5 space-y-2.5">
            {checks.map((c, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <Check className="w-4 h-4 flex-shrink-0" style={{ color: '#888' }} />
                <span className="text-[#aaa] text-sm font-normal">{c}</span>
              </div>
            ))}
          </div>
          <Link to="/contact" className="text-white text-[15px] mt-6 inline-flex items-center gap-1.5 hover:underline">Hear the AI Voice Agent <ArrowRight className="w-4 h-4" /></Link>
        </motion.div>
        <motion.div className="w-full lg:w-1/2 relative overflow-hidden min-h-[400px]" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
          <motion.img src={SS.voice} alt="AI Voice Agent" className="absolute inset-0 w-full h-full object-cover object-top" style={{ y: imgY }} />
        </motion.div>
      </div>
    </section>
  );
};

const FeatureGrid = () => (
  <section className="py-20 md:py-28 white-texture" style={{ background: '#111111' }} data-testid="echo-feature-grid">
    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-[1200px] mx-auto px-4">
      <motion.div variants={fadeUp} className="text-center mb-14">
        <p className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>Everything in Echo</p>
        <h2 className="text-3xl md:text-[48px] text-white font-bold tracking-tight mt-3">Built to keep every slot filled.</h2>
        <p className="text-[#aaa] text-base mt-3 font-normal">From scheduling to patient flow — fully automated.</p>
      </motion.div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {featureCards.map((card, i) => (
          <motion.div key={i} variants={fadeUp} className="rounded-2xl p-7 transition-colors duration-200 group" style={{ background: '#111111', border: '0.5px solid #2a2a2a' }} whileHover={{ borderColor: 'rgba(255,255,255,0.3)' }} data-testid={`echo-feature-card-${i}`}>
            <div className="h-20 rounded-lg overflow-hidden mb-4" style={{ background: '#1a1a1a' }}>
              <img src={card.img} alt={card.name} className="w-full h-full object-cover object-top" />
            </div>
            <h3 className="text-white text-lg font-bold mt-2">{card.name}</h3>
            <p className="text-[#aaa] text-sm leading-[1.6] mt-2 font-normal">{card.desc}</p>
          </motion.div>
        ))}
      </div>
    </motion.div>
  </section>
);

const FlowEditorialSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '8%']);
  return (
    <section ref={ref} className="w-full" style={{ background: '#0a0a0a' }} data-testid="echo-flow-editorial">
      <div className="flex flex-col lg:flex-row min-h-[600px]">
        <motion.div className="w-full lg:w-1/2 relative overflow-hidden min-h-[400px] order-2 lg:order-1" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
          <motion.img src={SS.flow} alt="Patient Flow" className="absolute inset-0 w-full h-full object-cover object-top" style={{ y: imgY }} />
        </motion.div>
        <motion.div className="w-full lg:w-1/2 px-8 md:px-16 lg:px-20 flex flex-col justify-center py-16 order-1 lg:order-2" initial={{ opacity: 0, x: 32 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
          <p className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>Patient Flow</p>
          <h2 className="text-3xl md:text-[48px] text-white font-bold leading-[1.15] tracking-tight max-w-[460px] mt-4">Real-time visibility into every patient.</h2>
          <p className="text-[17px] text-[#aaa] leading-[1.7] max-w-[420px] mt-4 font-normal">
            The Patient Flow board gives your entire team a real-time view of where every patient is — from check-in to checkout. Six color-coded columns, zero guessing.
          </p>
          <Link to="/contact" className="text-white text-[15px] mt-6 inline-flex items-center gap-1.5 hover:underline">Explore Patient Flow <ArrowRight className="w-4 h-4" /></Link>
        </motion.div>
      </div>
    </section>
  );
};

const PricingSection = () => (
  <section className="py-20 md:py-28 white-texture" style={{ background: '#111111', borderTop: '0.5px solid #2a2a2a' }} data-testid="echo-pricing">
    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-[900px] mx-auto px-4 text-center">
      <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>Flexible Usage Model</motion.p>
      <motion.h2 variants={fadeUpHeading} className="text-2xl md:text-3xl text-white font-bold tracking-tight mt-3">Pay only for what you use</motion.h2>
      <motion.p variants={fadeUp} className="text-[#aaa] text-base mt-3 max-w-[500px] mx-auto font-normal">Credits-based pricing. No long-term contracts required.</motion.p>
      <motion.div variants={fadeUp} className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
        {[
          { name: 'Lumina', credits: '50–100', desc: 'Per encounter. Ambient capture, SOAP note, and coding review.' },
          { name: 'RevQ', credits: '30–80', desc: 'Per claim. AI scrubbing, submission, denial management.' },
          { name: 'Echo', credits: '20–50', desc: 'Per scheduling action. Smart scheduling, AI phone, patient flow.' },
        ].map((p, i) => (
          <div key={i} className="rounded-xl p-6" style={{ background: '#1a1a1a', border: '0.5px solid #2a2a2a' }}>
            <div className="flex items-center gap-2 mb-3">
              <img src={`/screenshots/${p.name.toLowerCase()}_emblem_white.png`} alt={p.name} className="w-6 h-6 object-contain" />
              <span className="text-white text-base font-bold">{p.name}</span>
            </div>
            <div className="text-2xl text-white font-bold">{p.credits} <span className="text-sm font-normal text-[#555]">credits</span></div>
            <p className="text-[#aaa] text-sm mt-2 font-normal leading-relaxed">{p.desc}</p>
          </div>
        ))}
      </motion.div>
    </motion.div>
  </section>
);

const EcosystemBand = () => (
  <section className="py-14" style={{ background: '#0a0a0a', borderTop: '0.5px solid #2a2a2a' }} data-testid="echo-ecosystem">
    <div className="max-w-[600px] mx-auto px-4 text-center">
      <p className="text-[#555] text-xs uppercase tracking-[0.08em]">Part of the Aria One platform</p>
      <div className="flex items-center justify-center gap-12 mt-5">
        {['lumina', 'revq', 'echo'].map(name => (
          <img key={name} src={`/screenshots/${name}_logo_white.png`} alt={name} className="h-6 object-contain" style={{ opacity: 0.6 }} />
        ))}
      </div>
      <p className="text-[#555] text-sm mt-4 max-w-[500px] mx-auto font-normal">
        Echo schedules the visit. Lumina documents it. RevQ bills for it. One patient journey, three intelligent systems.
      </p>
    </div>
  </section>
);

const FinalCTA = () => (
  <section className="py-24 white-texture" style={{ background: '#111111', borderTop: '0.5px solid #2a2a2a' }} data-testid="echo-final-cta">
    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-[640px] mx-auto px-4 text-center">
      <motion.h2 variants={fadeUpHeading} className="text-3xl md:text-[40px] text-white font-bold tracking-tight">Ready to fill every slot?</motion.h2>
      <motion.p variants={fadeUp} className="text-[#aaa] text-base mt-3 font-normal">Start with Echo today. No long-term contract. Cancel anytime.</motion.p>
      <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 mt-8">
        <Link to="/contact" className="h-11 px-6 rounded-lg text-black text-sm font-medium flex items-center" style={{ background: '#ffffff' }}>Start free trial</Link>
        <button className="h-11 px-6 rounded-lg text-white text-sm font-medium flex items-center" style={{ border: '0.5px solid #2a2a2a' }}>Request a demo</button>
      </motion.div>
    </motion.div>
  </section>
);

const EchoPage = () => (
  <div style={{ background: '#0a0a0a' }}>
    <HeroSection />
    <StatBand />
    <HighlightCarousel />
    <StickyScrollSection />
    <FullWidthBreak />
    <VoiceEditorialSection />
    <FeatureGrid />
    <FlowEditorialSection />
    <PricingSection />
    <EcosystemBand />
    <FinalCTA />
  </div>
);

export default EchoPage;
